<template>
  <v-container class="pa-0 ma-0" fluid v-resize="onResize">
    <!-- Breadcrumb Navigation -->
    <v-card flat class="mb-2">
      <v-card-text class="pa-2">
        <v-breadcrumbs :items="breadcrumbs" class="pa-0">
          <template v-slot:item="{ item }">
            <v-breadcrumbs-item
              :disabled="item.disabled"
              :style="item.disabled ? 'cursor:default' : 'cursor:pointer'"
              @click="handleNavigation(item.path[item.path.length - 1])"
              class="text-subtitle-2">
              {{ item.text }}
            </v-breadcrumbs-item>
          </template>
          <template v-slot:divider>
            <v-icon>mdi-chevron-right</v-icon>
          </template>
        </v-breadcrumbs>
      </v-card-text>
    </v-card>

    <!-- Toolbar -->
    <v-card flat class="mb-2">
      <v-toolbar flat color="toolbar" height="60" style="cursor: default">
        <span>
          <v-toolbar-title>{{ currentFolderName }}</v-toolbar-title>
        </span>
        <v-divider class="mx-4" inset vertical></v-divider>
        
        <!-- Party Filter -->
        <v-col cols="3" class="pl-0">
          <PartySelect
            :selectedParties.sync="selectedParties"
            :includeOwnerRetailerAdGroups="true"
            @remove="removeParty">
          </PartySelect>
        </v-col>
        <v-col cols="2" v-if="files.length > 0">
          <v-select
            style="margin-top: 26px"
            dense
            outlined
            label="Order By"
            background-color="input"
            v-model="selectedOrderOption"
            :items="orderOptions"
            :menu-props="{ offsetY: true }">
            <template v-slot:append>
              <v-tooltip top>
                <template v-slot:activator="{ on: tooltip }">
                  <v-icon
                    class="pa-0"
                    v-on="tooltip"
                    @click.stop="handleIconClick"
                    @mousedown.stop>
                    {{sortAscending ? 'mdi-sort-descending' : 'mdi-sort-ascending'}}
                  </v-icon>
                </template>
                <span>Sort {{sortAscending ? 'Descending' : 'Ascending'}}</span>
              </v-tooltip>
            </template>
          </v-select>
        </v-col>
        
        <!-- Document Tags -->
        <v-col cols="auto" class="ml-auto">
          <v-menu
            v-model="filterMenu" 
            offset-y 
            :nudge-bottom="5" 
            :close-on-content-click="false">
            <template #activator="{ on }">
              <v-btn
                rounded 
                v-on="on" 
                text
                color="grey darken-2"
                class="ml-auto text-capitalize">
                <v-icon left>mdi-filter-outline</v-icon>
                Filter
                <v-badge
                  class="ml-1 mb-2"
                  color="accent"
                  :content="filterCount"
                  :value="filterCount > 0">
                </v-badge>
              </v-btn>
            </template>
            <v-card width="350">
              <v-card-text>
                <div class="mt-2">
                  <DocumentTags
                    v-model="tags"
                    @cancelLoad="() => loading = false"
                    :vertical="true">
                  </DocumentTags>
                </div>
              </v-card-text>
            </v-card>
          </v-menu>
        </v-col>
        
        <!-- Search -->
        <v-col cols="3" class="d-flex align-center">
          <v-text-field
            v-model="filterText"
            label="Search"
            placeholder="Search files and folders"
            hide-details="auto"
            background-color="input"
            dense
            outlined
            class="flex-grow-1 rounded-r-0"
            @keyup.enter="loadFiles(true)"
            clearable>
          </v-text-field>
          <v-btn
                  class="rounded-l-0"
                  color="primary"
                  depressed
                  height="40"
                  @click="loadFiles(true)"
          >
          <v-icon>mdi-magnify</v-icon>
        </v-btn>
        </v-col>

        <!-- View Toggle -->
        <v-col cols="auto">
          <v-btn-toggle v-model="viewMode" mandatory dense>
            <v-btn value="grid" small>
              <v-icon>mdi-view-grid</v-icon>
            </v-btn>
            <v-btn value="list" small>
              <v-icon>mdi-view-list</v-icon>
            </v-btn>
          </v-btn-toggle>
        </v-col>
      </v-toolbar>
    </v-card>

    <!-- File Drop Zone -->
    <v-card
      flat
      class="file-drop-zone"
      :class="{ 'drag-over': dragOver }"
      @drop="onDrop"
      @dragover.prevent="dragOver = true"
      @dragleave.prevent="dragOver = false"
      @dragenter.prevent
      style="min-height: 400px;">
      <v-card-text class="pa-2">
        <!-- Loading State -->
        <v-progress-linear v-if="loading" indeterminate></v-progress-linear>

        <template v-else>
          <!-- Grid View -->
          <div v-if="viewMode === 'grid'" class="file-grid">
            <v-row class="pb-16">
              <!-- Folders -->
              <v-col
                v-for="folder in filteredFolders"
                :key="folder.id"
                cols="auto"
                class="pa-2">
                  <v-tooltip top nudge-bottom="20px">
                    <template v-slot:activator="{on}">
                        <v-card
                          v-on="on"
                          class="folder-card"
                          hover
                          @click="handleNavigation(folder)"
                          @contextmenu.prevent="showContextMenu($event, folder, 'folder')">
                          <v-card-text class="text-center pa-3">
                            <v-icon size="48" color="primary">mdi-folder</v-icon>
                            <div class="text-subtitle-2 mt-2 folder-name">
                              {{ folder.name }}
                            </div>
                          </v-card-text>
                        </v-card>
                    </template>
                    <span>
                      {{ folder.name }}
                    </span>       
                  </v-tooltip>
                </v-col>
                
                  <!-- Files -->
                  <v-col
                  v-for="file in files"
                  :key="file.id"
                  cols="auto"
                  class="pa-2">
                    <v-tooltip top nudge-bottom="20px">
                      <template v-slot:activator="{on}">
                        <v-card
                          v-on="on"
                          class="file-card"
                          hover
                          @click="getDocDetails(file)"
                          @contextmenu.prevent="showContextMenu($event, file, 'file')">
                          <v-card-text class="text-center pa-3">
                            <v-icon size="48" :color="getFileIconColor(file)">
                              {{ getFileIcon(file) }}
                            </v-icon>
                            <div class="text-subtitle-2 mt-2 file-name">
                              {{ file.name }}
                            </div>
                            <div class="text-caption text--secondary">
                              {{ formatTimestamp(file.audit.updated_on) }}
                            </div>
                          </v-card-text>
                        </v-card>
                      </template>
                      <span>
                        {{ file.name }}
                      </span>
                    </v-tooltip>
                  </v-col>
              </v-row>
          </div>

          <!-- List View -->
          <div v-else class="file-list">
            <v-list class="pb-16" dense>
              <!-- Folders -->
              <v-list-item
                v-for="folder in filteredFolders"
                :key="folder.id"
                @click="handleNavigation(folder)"
                @contextmenu.prevent="showContextMenu($event, folder, 'folder')">
                <v-list-item-icon>
                  <v-icon color="primary">mdi-folder</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title>{{ folder.name }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>

              <!-- Files -->
              <v-list-item
                v-for="file in files"
                :key="file.id"
                @click="getDocDetails(file)"
                @contextmenu.prevent="showContextMenu($event, file, 'file')">
                <v-list-item-icon>
                  <v-icon :color="getFileIconColor(file)">
                    {{ getFileIcon(file) }}
                  </v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title>{{ file.name }}</v-list-item-title>
                  <v-list-item-subtitle>
                    {{ formatTimestamp(file.audit.updated_on) }}
                  </v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action>
                  <v-btn icon @click.stop="downloadFile(file.id)">
                    <v-icon>mdi-download</v-icon>
                  </v-btn>
                </v-list-item-action>
              </v-list-item>
            </v-list>
          </div>
        </template>

        <!-- Empty State -->
        <v-alert
          v-if="!loading && files.length === 0 && folders.length === 0"
          type="info"
          text
          class="ma-4">
        
        <!-- Empty folder state -->
          <div v-if="searchedText.length === 0 && folders.length === 0" class="text-center">
            <v-icon size="48" color="grey">mdi-folder-open</v-icon>
            <div class="text-h6 mt-2">This folder is empty</div>
            <div v-if="hasDocumentUploadAccess" class="text-subtitle-1">
              Drop files here to upload
            </div>
            <div v-if="!hasDocumentUploadAccess" class="text-subtitle-1">
              No Folders or Files found in this directory.
            </div>
          </div>
          <!-- No results state -->
          <div v-if="searchedText.length > 0" class="text-center">
            <v-icon size="48" color="grey">mdi-folder-open</v-icon>
            <div class="text-h6 mt-2">No Results</div>
            <div class="text-subtitle-1">
              No files or folders match your search criteria.
            </div>
          </div>
        </v-alert>

        <!-- Pagination -->
        <v-toolbar v-if="!loading && rawFileCount > 0" flat color="toolbar">
          <v-row justify="end" align="center">
            <v-col cols="2" sm="3" style="max-width:150px;">
              <v-select
                class="mt-8"
                dense
                :items="pageSizes"
                label="Items Per Page"
                v-model="pageSize"
                :menu-props="{ top: true, offsetY: true, maxHeight: 500 }">
              </v-select>
            </v-col>
            <v-btn
              icon
              large
              @click="page--"
              :disabled="page === 1"
              class="mx-2">
              <v-icon>mdi-chevron-left</v-icon>
            </v-btn>
            Page {{ page }}
            <v-btn icon large
              @click="page++"
              :disabled="rawFileCount < pageSize"
              class="mx-2">
            <v-icon>mdi-chevron-right</v-icon>
          </v-btn>
        </v-row>
        </v-toolbar>
      </v-card-text>
    </v-card>

    <!-- Context Menu -->
    <v-menu
      v-model="contextMenu.show"
      :position-x="contextMenu.x"
      :position-y="contextMenu.y"
      absolute
      offset-y
    >
      <v-list dense>
        <v-list-item v-if="contextMenu.type === 'file'" @click="downloadFile(contextMenu.item.id)">
          <v-list-item-icon>
            <v-icon>mdi-download</v-icon>
          </v-list-item-icon>
          <v-list-item-title>Download</v-list-item-title>
        </v-list-item>
        <v-list-item v-if="contextMenu.type === 'file' && isAdminUser" @click="openDeleteDialog(contextMenu.item)">
          <v-list-item-icon>
            <v-icon>mdi-delete</v-icon>
          </v-list-item-icon>
          <v-list-item-title>Delete</v-list-item-title>
        </v-list-item>
      </v-list>
    </v-menu>

    <!-- File Preview Dialog -->
    <v-dialog v-model="previewDialog" max-width="800">
      <v-card v-if="selectedFile">
        <v-card-title>
          {{ selectedFile.name }}
          <v-spacer></v-spacer>
          <v-btn icon @click="previewDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <div class="text-center">
            <v-icon size="64" :color="getFileIconColor(selectedFile)">
              {{ getFileIcon(selectedFile) }}
            </v-icon>
            <div class="text-h6 mt-2">{{ selectedFile.name }}</div>
            <div class="text-body-2 mt-2">{{ selectedFile.description }}</div>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-btn v-if="hasWriteAccess" @click="editFile(selectedFile)" color="primary">
            <v-icon left>mdi-pencil</v-icon>
            Edit
          </v-btn>
          <v-btn v-if="hasWriteAccess" @click="openDeleteDialog(selectedFile)" class="red white--text">
            <v-icon left>mdi-delete</v-icon>
            Delete
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn @click="downloadFile(selectedFile.id)" color="primary">
            <v-icon left>mdi-download</v-icon>
            Download
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete File Confirmation Dialog -->
    <v-dialog v-if="showDeleteDialog" v-model="showDeleteDialog" @click:outside="closeDeleteDialog()" width="450">
      <v-card>
        <v-card-title>Delete Document</v-card-title>
        <v-card-text>
          Delete "{{ selectedFile.name }}"?
        </v-card-text>
        <v-card-actions>
          <v-spacer/>
          <v-btn text @click="showDeleteDialog = false">Close</v-btn>
          <v-btn class="red white--text" @click.stop='deleteFile(selectedFile)'>Delete</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script lang="js">
// mixins
import { displayAlert } from '@/mixins/alert'
import { fullwidth } from '@/mixins/fullwidth'
import { userAccess } from '@/mixins/user-access'

// api
import document from '@/axios/document.ts'
import DocumentTag from '@/axios/document-tag.ts'
import file from '@/axios/file.ts'

// third party helpers
import { format } from 'date-fns'

// components
import DocumentTags from '@/components/DocumentTags.vue'
import PartySelect from '@/components/PartySelect.vue'

export default {
  name: 'FileExplorer',
  data: () => ({
    loading: true,
    filterMenu: false,
    page: 1,
    pageSize: 200,
    pageSizes: [50, 100, 200],
    totalItems: 0,
    viewMode: 'list',
    currentPath: [],
    files: [],
    rawFileCount: 0,
    selectedParties: [],
    selectedDocument: {},
    showDeleteDialog: false,
    filterText: '',
    searchedText: '',
    tags: {
      category: '',
      subcategory: '',
      subtype: '',
      status: '',
      ad_date: ''
    },
    dragOver: false,
    contextMenu: {
      show: false,
      x: 0,
      y: 0,
      item: null,
      type: null
    },
    previewDialog: false,
    selectedFile: null,
    selectedOrderOption: 'date',
    sortAscending: false,
    adDateTags: [],
    loadingAdDateTags: true
  }),
  mixins: [displayAlert, fullwidth, userAccess],
  components: { DocumentTags, PartySelect },
  watch: {
    selectedParties: {
      handler() {
        this.loadData()
      },
      deep: true
    },
    watchedTags: {
      handler(newValue) {
        const currentPathTags = this.pathToTags(this.currentPath)   
        if (JSON.stringify(newValue) !== JSON.stringify(currentPathTags)) {
          const path = this.tagsToPath(newValue)
          this.navigateToPath(path)
        }
      },
      deep: true
    },
    viewMode(newValue) {
      this.saveUserPreference('fileExplorerViewMode', newValue)
      this.$gtag.event('view_mode_change', {
        category: 'ui',
        view_mode: newValue
      })
    },
    page() {
      this.loadFiles()
    },
    pageSize() {
      this.page = 1
      this.loadFiles()
    },
    filterText (newValue, oldValue) {
      if ((newValue === '' || newValue === null) && oldValue) {
        this.page = 1
        this.searchedText = ''
        this.loadFiles()
      }
    },
    selectedOrderOption(newValue, oldValue) {
      if (newValue !== oldValue && newValue === 'name' && !this.sortAscending) {
        return this.sortAscending = true
      }
      this.loadFiles()
    },
    sortAscending() {
      this.loadFiles()
    }
  },
  async created() {
    try {
      this.loadUserPreferences()
      const res = await DocumentTag.getAdDateTags()
      this.adDateTags = res.data
      this.loadingAdDateTags = false
      await this.loadData()
    } catch (error) {
      this.handleError(error)
      this.loadingAdDateTags = false
      this.adDateTags = []
    }
  },
  computed: {
    watchedTags() {
      return { ...this.tags }
    },
    breadcrumbs() {
      const breadcrumbs = [
        { text: 'Files', path: [], disabled: false }
      ]
      this.currentPath.forEach((path, index) => {
        breadcrumbs.push({
          text: path.type === 'ad_date' ? `Ad Date: ${path.name}` : path.name,
          path: this.buildPath(path),
          disabled: index === this.currentPath.length - 1
        })
      })
      return breadcrumbs
    },
    currentFolder() {
      return this.currentPath.length > 0 ? this.currentPath[this.currentPath.length - 1] : null
    },
    currentFolderName() {
      if (this.currentFolder) {
        const { name, type } = this.currentFolder
        return type === 'ad_date' ? `Ad Date: ${name}` : name
      }
      return 'Files'
    },
    filteredIds() {
      return this.userRestrictedSubcategories
    },
    filterCount() {
      return Object.values(this.tags).filter(Boolean).length || 0
    },
    folders() {
      if (this.currentPath?.length > 0) {
        const currentFolder = this.currentPath[this.currentPath.length - 1]
        if (currentFolder) {
          return currentFolder.children || []
        }
      }
      return this.categoryFolders
    },
    categoryFolders() {
      if (this.loadingAdDateTags) return []
      return this.userCategories.map(category => {
        return {
          id: category.id,
          name: category.name,
          type: 'category',
          children: this.buildChildFolders(category)
        }
      })
    },
    filteredFolders() {
      const searchTerm = this.searchedText?.toLowerCase() || '';
      return [...this.folders]
        .filter(folder => folder.name.toLowerCase().includes(searchTerm))
        .sort((a, b) => a.type === 'ad_date' ? (Date.parse(b.id) - Date.parse(a.id)) : a.name.localeCompare(b.name));
    },
    filteredFiles() {
      const searchTerm = this.filterText?.toLowerCase() || ''
      return this.files.filter(file => 
        file.name.toLowerCase().includes(searchTerm)
      );
    },
    fromPage() {
      return (this.page - 1) * this.pageSize;
    },
    paginatedFiles() {
      return this.files
    },
    totalFiles() {
      return this.totalItems
    },
    orderOptions() {
      return [
        { text: 'Name', value: 'name' },
        { text: 'Updated On', value: 'date' }
      ]
    }
  },
  methods: {
    handleIconClick() {
      return this.sortAscending = !this.sortAscending
    },
    buildChildFolders(parentFolder) {
      return (parentFolder?.children || []).flatMap(child => {
        if (!child) return []
        
        if (child?.type === 'tag_key') {
          const type = child.id.toLowerCase()
          return child.children.flatMap(c => {
            if (!c || (type === 'subcategory' && this.filteredIds.includes(c.id))) return []
            let children = this.buildChildFolders(c)
            if (type === 'subtype') {
              children = this.buildAdDateFolders(c.id)
            }
            return {
              id: c.id,
              name: c.name,
              type: type,
              children
            }
          })
        }
        return []
      })
    },
    buildAdDateFolders(subtype) {
      const folderRef = this.adDateTags.find(f => f?.subtype === subtype)
      if (folderRef?.ad_dates?.length > 0) {
        return folderRef?.ad_dates.map(date => {
          const [year, month, day] = date.split('-')
          const dateStr = `${month}/${day}/${year}`
          return {
            id: date,
            name: dateStr,
            type: 'ad_date',
            subtype: folderRef.subtype,
            subcategory: folderRef.subcategory,
            category: folderRef.category,
            children: []
          }
        })
      }
      return []
    },
    loadUserPreferences() {
      const savedViewMode = localStorage.getItem('fileExplorerViewMode')
      if (savedViewMode) {
        this.viewMode = savedViewMode
      }
    },
    saveUserPreference(key, value) {
      localStorage.setItem(key, value)
    },
    async loadData() {
      this.loading = true
      try {
        // If there is any search text, treat this as a search
        const isSearching = !!this.filterText
        await this.loadFiles(isSearching)
      } catch (error) {
        this.handleError(error)
      } finally {
        this.loading = false
      }
    },
    buildTagParams() {
      if (this.currentPath.length === 0) return {}

      if (this.currentPath.length === 1) {
        return {
          category: this.currentPath[0].id,
          status: 'CURRENT'
        }
      }
      
      const params = {
        category: this.currentPath[0].id,
        subcategory: this.currentPath[1].id,
        status: 'CURRENT'
      };
      
      if (this.currentPath.length > 2) {
        params.subtype = this.currentPath[2].id
      }
      if (this.currentPath.length > 3) {
        params.ad_date = this.currentPath[3].id
      }
      
      return params
    },
    // The search API treats unspecified tags as unconstrained, so a folder-scoped
    // request also returns documents that live deeper in the tree. A document
    // belongs to the folder we are viewing when its next-level tag is empty, or
    // when that tag names a value with no matching subfolder — in the latter case
    // there is nowhere deeper to reach it, so surface it here instead of losing it.
    isAtCurrentLevel(doc) {
      const tags = doc?.tags || {}
      const childTagKey = ['subcategory', 'subtype', 'ad_date'][this.currentPath.length - 1]
      // ad_date is a leaf; nothing nests below it.
      if (!childTagKey) return true
      const value = tags[childTagKey]
      if (value === undefined || value === null || value === '') return true
      return !this.folders.some(folder => folder.id === value)
    },

    async loadFiles(searching = false) {
      if (!searching) {
        // Load files from the category level down. Only the root is skipped, where
        // buildTagParams yields {} and the query would be unscoped. Every folder,
        // including one with subfolders, lists the documents that belong to it;
        // isAtCurrentLevel separates those from documents that live deeper.
        if (this.currentPath.length === 0) {
          this.files = []
          this.rawFileCount = 0
          this.totalItems = 0
          return;
        }
      } else {
        this.page = 1
        this.searchedText = this.filterText
      }
      const orderBy = `${this.selectedOrderOption} ${this.sortAscending ? 'ascending' : 'descending'}`
      try {
        this.loading = true
        const tags = this.buildTagParams()
        const partyIds = this.buildPartyIds()
        const data = { 
          party_ids: partyIds,
          excluded_subcategories: this.userRestrictedSubcategories,
          filter_text: this.filterText,
          order_by: orderBy
        }
        const res = await document.search(
          data,
          tags,
          this.fromPage,
          this.pageSize
        )
        const rawDocs = Array.isArray(res.data) ? res.data : []
        // The search API treats unspecified tags as unconstrained, so a request
        // scoped to a folder also returns its descendants' documents. Keep only
        // the ones belonging to this folder when browsing; a search is meant to
        // span the whole tree.
        this.rawFileCount = rawDocs.length
        this.files = rawDocs
          .filter(doc => searching || this.isAtCurrentLevel(doc))
          .map(doc => ({
            id: doc.id,
            name: doc.name,
            description: doc.description,
            size: doc.document_file?.size || 0,
            mimeType: doc.document_file?.mime_type || 'application/octet-stream',
            audit: doc.audit,
            tags: doc.tags || {}
          }))
        this.totalItems = res.meta?.total_count || this.files.length
        this.pageCount = Math.ceil(this.files.length / this.pageSize)
      } catch (error) {
        this.handleError(error)
        this.files = []
        this.rawFileCount = 0
        this.totalItems = 0
        this.pageCount = 0
      } finally {
        this.loading = false
      }
    },
    buildPartyIds() {
      let partyIds = this.userPartyId ? [this.userPartyId] : []
      if (this.selectedParties.length > 0) {
        partyIds.push(...this.selectedParties.map(party => party.party_id || party.id))
      }
      if (partyIds.length === 0 && this.isNonAdminUser) {
        partyIds = this.userSearchPartyIds
      }
      return partyIds
    },
    navigateToPath(path) {
      this.currentPath = path
      this.tags = this.pathToTags(path)
      this.loadData()
    },
    handleNavigation(folder) {
      this.page = 1
      this.filterText = ''
      this.searchedText = ''
      const path = this.buildPath(folder)
      this.$gtag.event('folder_navigate', {
        category: 'navigation',
        folder_level: folder?.type || 'root',
        folder_name: folder?.name || 'Files'
      })
      this.navigateToPath(path)
    },
    buildPath(folder) {
      let path = []
      if (folder != null) {
        if (folder.type === 'category') {
          path = [folder]
        } else if (folder.type === 'subcategory') {
          const category = this.categoryFolders.find(category => {
            return category.children.some(child => child.id === folder.id)
          })
          path = [category, folder]
        } else if (folder.type === 'subtype') {
          path = this.categoryFolders.flatMap(category => {
            const subcat = category.children.find(subcategory => {
              if (subcategory.children?.length > 0) {
                return subcategory.children.some(child => child.id === folder.id)
              }
            })
            return (subcat) ? [category, subcat] : []
          })
          path = [...path, folder]
        } else if (folder.type === 'ad_date') {
          const category = this.findFolder(folder.category, this.categoryFolders)
          const subcat = this.findFolder(folder.subcategory, category.children)
          const subtype = this.findFolder(folder.subtype, subcat.children)
          path = [category, subcat, subtype, folder]
        }
      }
      return path
    },
    pathToTags(path) {
      if (path.length > 0) {
        return path.reduce((obj, folder) => {
          obj[folder.type] = folder.id
          return obj
        }, {})
      }
      return {}
    },
    tagsToPath(tags) {
      const category = this.categoryFolders.find(f => f.id === tags.category)
      const subcategory = category?.children?.find(c => c?.id === tags.subcategory)
      const subtype = subcategory?.children?.find(c => c?.id === tags.subtype)
      if (!subcategory) {
        tags.subtype = null
        tags.subcategory = null
        tags.ad_date = null
      } else if (!subtype) {
        tags.subtype = null
        tags.ad_date = null
      }
      if (subtype?.children?.length === 0) {
        tags.ad_date = null
      }
      const id = tags.ad_date || tags.subtype || tags.subcategory || tags.category
      const folder = id ? this.findFolder(id, this.categoryFolders) : null
      const path = this.buildPath(folder)
      return path
    },
    findFolder(id, folders) {
      if (!Array.isArray(folders)) return null
      for (const folder of folders) {
        if (folder.id === id) return folder
        const result = this.findFolder(id, folder.children)
        if (result) return result
      }
      return null
    },
    getDocDetails (doc) {
      if (this.hasWriteAccess) {
        this.previewDialog = true
        this.selectedFile = doc
      } else {
        return this.downloadFile(doc.id)
      }
    },
    editFile(file) {
      return this.$router.push({
        name: 'Edit',
        query: { id: file.id }
      })
    },
    openDeleteDialog(item) {
      this.showDeleteDialog = true
      this.selectedFile = item
    },
    closeDeleteDialog() {
      this.showDeleteDialog = false
      if (!this.previewDialog) {
        this.selectedFile = {}
      }
    },
    async downloadFile(id) {
      try {
        const res = await file.get(id)
        const url = res.data?.url
        if (url) {
          const newWindow = window.open(url, '_blank')
          if (newWindow) {
            newWindow.opener = null
          }
          this.$gtag.event('document_download', {
            category: 'documents',
            document_id: id
          })
        }
      } catch (err) {
        this.handleError(err)
      }
    },
    async deleteFile(file) {
      try {
        await document.delete(file.id)
        this.emitAlert(true, 'success', `${file.name} has been successfully deleted`)
        this.$gtag.event('document_delete', {
          category: 'documents',
          document_id: file.id
        })
        this.loadData()
        if (this.previewDialog) {
          this.previewDialog = false
        }
        this.selectedFile = {}
        this.showDeleteDialog = false
      } catch (err) {
        this.handleError(err)
      }
    },
    showContextMenu(event, item, type) {
      this.contextMenu.show = false
      this.contextMenu.x = event.clientX
      this.contextMenu.y = event.clientY
      this.contextMenu.item = item
      this.contextMenu.type = type
      this.$nextTick(() => {
        this.contextMenu.show = true
      })
    },
    onDrop(event) {
      event.preventDefault()
      this.dragOver = false
      
      const files = event.dataTransfer.files
      if (files.length > 0 && this.hasDocumentUploadAccess && this.currentPath.length === 2) {
        this.uploadFiles(files)
      }
    },
    uploadFiles() {
      // Navigate to upload page with current context
      this.$router.push({
        name: 'Upload',
        query: {
          category: this.currentPath[0].id,
          subcategory: this.currentPath[1].id
        }
      })
    },
    getFileIcon(file) {
      const mimeType = file.mimeType || ''
      if (mimeType.startsWith('image/')) return 'mdi-file-image'
      if (mimeType.startsWith('video/')) return 'mdi-file-video'
      if (mimeType.startsWith('audio/')) return 'mdi-file-music'
      if (mimeType.includes('pdf')) return 'mdi-file-pdf-box'
      if (mimeType.includes('word')) return 'mdi-file-word'
      if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) return 'mdi-file-excel'
      if (mimeType.includes('powerpoint') || mimeType.includes('presentation')) return 'mdi-file-powerpoint'
      if (mimeType.includes('zip') || mimeType.includes('archive')) return 'mdi-file-archive'
      return 'mdi-file-document'
    },
    getFileIconColor(file) {
      const mimeType = file.mimeType || ''
      if (mimeType.startsWith('image/')) return 'green'
      if (mimeType.startsWith('video/')) return 'red'
      if (mimeType.startsWith('audio/')) return 'purple'
      if (mimeType.includes('pdf')) return 'red'
      if (mimeType.includes('word')) return 'blue'
      if (mimeType.includes('excel')) return 'green'
      if (mimeType.includes('powerpoint')) return 'orange'
      return 'grey'
    },
    formatFileSize(bytes) {
      if (!bytes) return '0 B'
      const k = 1024
      const sizes = ['B', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    },
    formatTimestamp(time) {
      if (!time) return ''
      const date = new Date(time)
      return format(date, 'MMM d, yyyy')
    },
    removeParty(index) {
      this.selectedParties.splice(index, 1)
    },
    onResize() {
      // Handle window resize if needed
    }
  }
}
</script>

<style scoped lang="scss">
.file-drop-zone {
  border: 2px dashed transparent;
  transition: all 0.3s ease;
  
  &.drag-over {
    border-color: var(--v-primary-base);
    background-color: rgba(25, 118, 210, 0.05);
  }
}

.file-grid {
  .folder-card, .file-card {
    width: 120px;
    height: 120px;
    cursor: pointer;
    transition: all 0.2s ease;
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 8px rgba(0,0,0,0.12);
    }
  }
  
  .folder-name, .file-name {
    word-break: break-word;
    line-height: 1.2;
    max-height: 2.4em;
    overflow: hidden;
    display: -webkit-box;
    line-clamp: 2;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }
}

.file-list {
  .v-list-item {
    cursor: pointer;
    
    &:hover {
      background-color: rgba(0,0,0,0.04);
    }
  }
}
.v-card .v-toolbar:nth-child(2) {
  position: fixed;
  width: 100%;
  bottom: 1.95rem;
  right: .25rem;
}
.no-hover::before {
  background-color: transparent !important;
}
</style>